import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Bell, Mail, Phone, Info } from 'lucide-react';

import { Section } from '@/components/primitives/Section';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';

// Legacy JS api module — typed via legacy-shims.d.ts.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
import * as notificationsApi from '@/api/notifications';

interface Preferences {
  email_daily_digest: boolean;
  email_weekly_summary: boolean;
  email_admin_report: boolean;
  email_renewal_reminders: boolean;
  email_wildfire_alerts: boolean;
  email_assignment_notifications: boolean;
  email_moderation_reminders: boolean;
  sms_enabled: boolean;
  phone_number?: string | null;
  inapp_enabled: boolean;
}

const EMAIL_TOGGLES: { key: keyof Preferences; label: string; hint: string }[] = [
  {
    key: 'email_daily_digest',
    label: 'Daily fire-risk digest',
    hint: 'A morning email with today’s FDI, wind, and active incidents.',
  },
  {
    key: 'email_weekly_summary',
    label: 'Weekly FPA summary',
    hint: 'Monday digest — incident count, response times, member changes.',
  },
  {
    key: 'email_admin_report',
    label: 'Monthly admin report',
    hint: 'Cross-tenant activity overview. Sent to super-admins only.',
  },
  {
    key: 'email_renewal_reminders',
    label: 'Membership renewal reminders',
    hint: '30 / 14 / 7-day reminders before a member’s payment is due.',
  },
  {
    key: 'email_wildfire_alerts',
    label: 'Wildfire alerts (Phase 2)',
    hint: 'New wildfire in your FPA + severity-change alerts.',
  },
  {
    key: 'email_assignment_notifications',
    label: 'Firefighter assignments (Phase 2)',
    hint: 'When you’re assigned to an incident.',
  },
  {
    key: 'email_moderation_reminders',
    label: 'Moderation reminders (Phase 2)',
    hint: 'Daily reminder when pending images/videos await your review.',
  },
];

export default function NotificationPreferences() {
  const [prefs, setPrefs] = useState<Preferences | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  // Holds the phone-number edit before save so we can debounce-write
  // when the user finishes typing rather than on every keystroke.
  const [phoneInput, setPhoneInput] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = (await notificationsApi.getMyPreferences()) as unknown as Preferences;
        if (cancelled) return;
        setPrefs(data);
        setPhoneInput(data.phone_number ?? '');
      } catch (err) {
        if (cancelled) return;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const e = err as any;
        setLoadError(
          e?.response?.data?.detail || e?.message || 'Failed to load preferences',
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const patch = async (next: Partial<Preferences>) => {
    if (!prefs) return;
    setSaving(true);
    try {
      const updated = (await notificationsApi.updateMyPreferences(next)) as unknown as Preferences;
      setPrefs(updated);
      toast.success('Preferences saved');
    } catch (err) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const e = err as any;
      toast.error(e?.response?.data?.detail || e?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loadError) {
    return (
      <Alert variant="destructive">
        <AlertDescription>{loadError}</AlertDescription>
      </Alert>
    );
  }

  if (!prefs) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Section
        title={
          <span className="flex items-center gap-2">
            <Mail className="h-4 w-4 text-muted-foreground" />
            Email
          </span>
        }
        description="Transactional emails (invites, shared reports) always send regardless of these toggles."
      >
        <div className="space-y-3">
          {EMAIL_TOGGLES.map((row) => (
            <div
              key={row.key}
              className="flex items-start justify-between gap-3 rounded-md border p-3"
            >
              <div className="min-w-0 flex-1">
                <Label className="text-sm font-medium">{row.label}</Label>
                <p className="mt-0.5 text-xs text-muted-foreground">{row.hint}</p>
              </div>
              <Switch
                checked={Boolean(prefs[row.key])}
                disabled={saving}
                onCheckedChange={(v) => patch({ [row.key]: v } as Partial<Preferences>)}
              />
            </div>
          ))}
        </div>
      </Section>

      <Section
        title={
          <span className="flex items-center gap-2">
            <Phone className="h-4 w-4 text-muted-foreground" />
            SMS
            <Badge variant="outline" className="ml-1">
              Coming soon
            </Badge>
          </span>
        }
        description="Phase 2 will light up SMS delivery via AWS SNS. Save your phone number now and we’ll switch you on when it ships."
      >
        <div className="space-y-3">
          <div className="flex items-start justify-between gap-3 rounded-md border p-3">
            <div className="min-w-0 flex-1">
              <Label className="text-sm font-medium">Receive SMS for critical alerts</Label>
              <p className="mt-0.5 text-xs text-muted-foreground">
                When enabled, opt-in for SMS on top of your email preferences. No
                SMS is actually sent yet — today the system logs a "Phase 2"
                marker against your notification history.
              </p>
            </div>
            <Switch
              checked={Boolean(prefs.sms_enabled)}
              disabled={saving}
              onCheckedChange={(v) => patch({ sms_enabled: v })}
            />
          </div>

          <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:gap-3">
            <div className="flex-1">
              <Label htmlFor="phone-input" className="text-sm">
                Phone number
              </Label>
              <Input
                id="phone-input"
                value={phoneInput}
                onChange={(e) => setPhoneInput(e.target.value)}
                placeholder="+27 82 123 4567"
                inputMode="tel"
              />
            </div>
            <Button
              variant="outline"
              disabled={saving || phoneInput === (prefs.phone_number ?? '')}
              onClick={() => patch({ phone_number: phoneInput || null })}
            >
              Save number
            </Button>
          </div>
        </div>
      </Section>

      <Section
        title={
          <span className="flex items-center gap-2">
            <Bell className="h-4 w-4 text-muted-foreground" />
            In-app
            <Badge variant="outline" className="ml-1">
              Coming soon
            </Badge>
          </span>
        }
      >
        <div className="flex items-start justify-between gap-3 rounded-md border p-3">
          <div>
            <Label className="text-sm font-medium">Show in-app notification badge</Label>
            <p className="mt-0.5 text-xs text-muted-foreground">
              Persistent bell in the app shell with unread counts. Phase 2.
            </p>
          </div>
          <Switch
            checked={Boolean(prefs.inapp_enabled)}
            disabled={saving}
            onCheckedChange={(v) => patch({ inapp_enabled: v })}
          />
        </div>
      </Section>

      <Alert>
        <Info className="h-4 w-4" />
        <AlertDescription>
          Every email we send is recorded in your notification history. Phase 2
          will add an in-app feed; for now the audit trail lives server-side and
          is visible via the admin debugger.
        </AlertDescription>
      </Alert>
    </div>
  );
}
